import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useProjectContext } from './ProjectContext';
import apiClient from '../services/apiClient';

/**
 * TeamManagementContext - Centralized team management data
 * Eliminates prop drilling by providing user data, departments, managers, and permissions
 * through React context. Follows the established hooks + context pattern.
 */
const TeamManagementContext = createContext();

export const useTeamManagementContext = () => {
    const context = useContext(TeamManagementContext);
    if (!context) {
        throw new Error('useTeamManagementContext must be used within a TeamManagementProvider');
    }
    return context;
};

export const useTeamManagementContextOptional = () => {
    return useContext(TeamManagementContext);
};

export const TeamManagementProvider = ({ children }) => {
    const { user, token, loading: authLoading } = useAuth();
    const { projects } = useProjectContext();

    // Data state
    const [users, setUsers] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [managers, setManagers] = useState([]);

    // Loading and error state
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');

    const USER_DATA_CACHE_TTL_MS = 300000;
    const usersCache = useRef({ data: null, timestamp: 0, inFlight: null });

    // Dialog states
    const [dialogStates, setDialogStates] = useState({
        userDialog: false,
        assignmentDialog: false
    });

    // Computed permissions (centralized)
    const canListUsers = Boolean(
        user?.is_superuser ||
        ['admin', 'director', 'hr_manager'].includes(user?.role)
    );
    // `user` may be recreated (new object identity) by unit-test mocks, which
    // can cause effects that depend on `user` to re-run every render and
    // trigger infinite update loops. Track only stable primitives instead.
    const hasUser = Boolean(user);

    const permissions = {
        canListUsers,
        canManageUsers: user?.role === 'admin' ||
            user?.role === 'manager' ||
            user?.role === 'director' ||
            user?.is_superuser,
        canAssignToProjects: user?.role === 'project_manager' ||
            user?.role === 'admin' ||
            user?.role === 'manager' ||
            user?.is_superuser,
        canManageSuperusers: user?.role === 'admin' || user?.is_superuser,
        canViewSalaryInfo: user?.role === 'admin' ||
            user?.role === 'manager' ||
            user?.role === 'director' ||
            user?.is_superuser
    };

    // Cache for departments and managers (5 minutes)
    const departmentsCache = useRef({ data: null, timestamp: 0 });
    const managersCache = useRef({ data: null, timestamp: 0 });

    const fetchUsers = useCallback(async (forceRefresh = false) => {
        if (!canListUsers) {
            setUsers([]);
            setManagers([]);
            return [];
        }

        const now = Date.now();

        if (!forceRefresh && usersCache.current.data &&
            (now - usersCache.current.timestamp) < USER_DATA_CACHE_TTL_MS) {
            setUsers(usersCache.current.data);
            const cachedManagers = usersCache.current.data.filter(userItem => userItem.role === 'manager');
            setManagers(cachedManagers);
            managersCache.current = {
                data: cachedManagers,
                timestamp: usersCache.current.timestamp
            };
            return usersCache.current.data;
        }

        if (!forceRefresh && usersCache.current.inFlight) {
            return usersCache.current.inFlight;
        }

        const inFlight = (async () => {
            try {
                setLoading(true);
                setError(null);
                const response = await apiClient.get('/users/', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const data = response?.data?.results || response?.data || [];
                setUsers(data);

                const managersFromUsers = data.filter(userItem => userItem.role === 'manager');
                setManagers(managersFromUsers);
                managersCache.current = {
                    data: managersFromUsers,
                    timestamp: Date.now()
                };

                usersCache.current = {
                    data,
                    timestamp: Date.now()
                };
                return data;
            } catch (err) {
                console.error('Error fetching users:', err);
                setUsers([]);
                setManagers([]);
                if (err.response?.status !== 403) {
                    setError('Error loading users');
                }
                return [];
            } finally {
                setLoading(false);
            }
        })();

        usersCache.current.inFlight = inFlight;
        try {
            return await inFlight;
        } finally {
            if (usersCache.current.inFlight === inFlight) {
                usersCache.current.inFlight = null;
            }
        }
    }, [token, canListUsers]);

    const fetchDepartments = useCallback(async (forceRefresh = false) => {
        // Check cache first (5 minutes TTL)
        const now = Date.now();
        if (!forceRefresh && departmentsCache.current.data &&
            (now - departmentsCache.current.timestamp) < 300000) {
            setDepartments(departmentsCache.current.data);
            return;
        }

        try {
            const response = await apiClient.get('/departments/', {
                headers: { Authorization: `Bearer ${token}` }
            });
            const responseData = response?.data || {};
            const data = responseData.results || responseData || [];
            setDepartments(data);

            // Update cache
            departmentsCache.current = { data, timestamp: now };
        } catch (err) {
            console.error('Error fetching departments:', err);
            setDepartments([]);
        }
    }, [token]);

    const fetchManagers = useCallback(async (forceRefresh = false) => {
        if (users.length > 0) {
            const data = users.filter(userItem => userItem.role === 'manager');
            setManagers(data);
            managersCache.current = {
                data,
                timestamp: Date.now()
            };
            return data;
        }

        // Check cache first (5 minutes TTL)
        const now = Date.now();
        if (!forceRefresh && managersCache.current.data &&
            (now - managersCache.current.timestamp) < 300000) {
            setManagers(managersCache.current.data);
            return;
        }

        try {
            if (!canListUsers) {
                setManagers([]);
                return [];
            }

            const response = await apiClient.get('/users/?role=manager', {
                headers: { Authorization: `Bearer ${token}` }
            });
            const responseData = response?.data || {};
            const data = responseData.results || responseData || [];
            setManagers(data);

            // Update cache
            managersCache.current = { data, timestamp: now };
        } catch (err) {
            console.error('Error fetching managers:', err);
            setManagers([]);
        }
    }, [token, users, canListUsers]);

    React.useEffect(() => {
        if (authLoading || !token || !hasUser) {
            if (!authLoading) setLoading(false);
            return;
        }
        const initTeamData = async () => {
            try {
                if (canListUsers) {
                    await fetchUsers();
                } else {
                    setUsers([]);
                    setManagers([]);
                }
                await Promise.all([
                    fetchDepartments(),
                    fetchManagers()
                ]);
            } catch (err) {
                console.error('Team management initialization failed:', err);
                setError('Failed to initialize team data');
            } finally {
                setLoading(false);
            }
        };

        void initTeamData().catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, hasUser, authLoading, canListUsers]);

    // Dialog management
    const dialogOperations = {
        openUserDialog: useCallback(() => {
            setDialogStates(prev => ({ ...prev, userDialog: true }));
        }, []),

        closeUserDialog: useCallback(() => {
            setDialogStates(prev => ({ ...prev, userDialog: false }));
        }, []),

        openAssignmentDialog: useCallback(() => {
            setDialogStates(prev => ({ ...prev, assignmentDialog: true }));
        }, []),

        closeAssignmentDialog: useCallback(() => {
            setDialogStates(prev => ({ ...prev, assignmentDialog: false }));
        }, [])
    };

    // User operations with context updates
    const userOperations = {
        create: useCallback(async (userData) => {
            try {
                setError(null);
                const response = await apiClient.post('/users/', userData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setUsers(prev => [...prev, response.data]);
                setSuccessMessage('User created successfully');
                return response.data;
            } catch (err) {
                console.error('Error creating user:', err);
                const errorMessage = err.response?.data?.detail || 'Failed to create user';
                setError(errorMessage);
                throw new Error(errorMessage);
            }
        }, [token]),

        update: useCallback(async (userId, userData) => {
            try {
                setError(null);
                const response = await apiClient.put(`/users/${userId}/`, userData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setUsers(prev => prev.map(user =>
                    user.id === userId ? response.data : user
                ));
                setSuccessMessage('User updated successfully');
                return response.data;
            } catch (err) {
                console.error('Error updating user:', err);
                const errorMessage = err.response?.data?.detail || 'Failed to update user';
                setError(errorMessage);
                throw new Error(errorMessage);
            }
        }, [token]),

        delete: useCallback(async (userId) => {
            try {
                setError(null);
                await apiClient.delete(`/users/${userId}/`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setUsers(prev => prev.filter(user => user.id !== userId));
                setSuccessMessage('User deleted successfully');
            } catch (err) {
                console.error('Error deleting user:', err);
                const errorMessage = err.response?.data?.detail || 'Failed to delete user';
                setError(errorMessage);
                throw new Error(errorMessage);
            }
        }, [token]),

        setPassword: useCallback(async (userId, newPassword, confirmPassword) => {
            try {
                setError(null);
                await apiClient.post(
                    `/users/${userId}/set-password/`,
                    {
                        new_password: newPassword,
                        confirm_password: confirmPassword,
                    },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                setSuccessMessage('Password updated successfully');
            } catch (err) {
                console.error('Error setting user password:', err);
                const errorData = err.response?.data;
                const errorMessage = errorData?.confirm_password?.[0]
                    || errorData?.new_password?.[0]
                    || errorData?.detail
                    || errorData?.error
                    || 'Failed to update password';
                setError(errorMessage);
                throw new Error(errorMessage);
            }
        }, [token]),

        toggleStatus: useCallback(async (userId, currentStatus) => {
            try {
                setError(null);
                const response = await apiClient.patch(`/users/${userId}/`, {
                    is_active: !currentStatus
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setUsers(prev => prev.map(user =>
                    user.id === userId ? { ...user, is_active: !currentStatus } : user
                ));
                setSuccessMessage(`User ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
                return response.data;
            } catch (err) {
                console.error('Error toggling user status:', err);
                const errorMessage = err.response?.data?.detail || 'Failed to update user status';
                setError(errorMessage);
                throw new Error(errorMessage);
            }
        }, [token]),

        updateField: useCallback(async (userId, field, value) => {
            try {
                setError(null);
                const response = await apiClient.patch(`/users/${userId}/`, {
                    [field]: value
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setUsers(prev => prev.map(user =>
                    user.id === userId ? { ...user, [field]: value } : user
                ));
                setSuccessMessage(`User ${field} updated successfully`);
                return response.data;
            } catch (err) {
                console.error(`Error updating user ${field}:`, err);
                const errorMessage = err.response?.data?.detail || `Failed to update user ${field}`;
                setError(errorMessage);
                throw new Error(errorMessage);
            }
        }, [token]),

        assignToProject: useCallback(async (projectId, userId) => {
            try {
                setError(null);
                await apiClient.post(`/projects/${projectId}/assign_user/`,
                    { user_id: userId },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                setSuccessMessage('User assigned to project successfully');
            } catch (err) {
                console.error('Error assigning user to project:', err);
                const errorMessage = err.response?.data?.detail || 'Failed to assign user to project';
                setError(errorMessage);
                throw new Error(errorMessage);
            }
        }, [token]),

        removeFromProject: useCallback(async (projectId, userId) => {
            try {
                setError(null);
                await apiClient.delete(`/projects/${projectId}/remove_user/`, {
                    data: { user_id: userId },
                    headers: { Authorization: `Bearer ${token}` }
                });
                setSuccessMessage('User removed from project successfully');
            } catch (err) {
                console.error('Error removing user from project:', err);
                const errorMessage = err.response?.data?.detail || 'Failed to remove user from project';
                setError(errorMessage);
                throw new Error(errorMessage);
            }
        }, [token])
    };

    // Utility functions
    const getUserById = useCallback((userId) => {
        return users.find(u => u.id === userId);
    }, [users]);

    const getUsersByRole = useCallback((role) => {
        return users.filter(u => u.role === role);
    }, [users]);

    const getActiveUsers = useCallback(() => {
        return users.filter(u => u.is_active);
    }, [users]);

    const getUsersByDepartment = useCallback((departmentId) => {
        return users.filter(u => u.department === departmentId);
    }, [users]);

    const getUserProjects = useCallback((userId) => {
        return projects.filter(project =>
            project.team_members?.some(member => member.id === userId)
        );
    }, [projects]);

    const getAvailableProjects = useCallback((userId) => {
        return projects.filter(project =>
            !project.team_members?.some(member => member.id === userId)
        );
    }, [projects]);

    const clearMessages = useCallback(() => {
        setError(null);
        setSuccessMessage('');
    }, []);

    // Refresh operations
    const refreshData = useCallback(async () => {
        await fetchUsers(true);
        await Promise.all([
            fetchDepartments(),
            fetchManagers(true)
        ]);
    }, [fetchUsers, fetchDepartments, fetchManagers]);

    const contextValue = {
        // Data
        users,
        projects,
        departments,
        managers,

        // Loading states
        loading,
        error,
        successMessage,

        // Dialog states
        dialogStates,

        // Permissions
        permissions,

        // Operations
        userOperations,
        dialogOperations,

        // Fetch operations
        fetchUsers,
        fetchDepartments,
        fetchManagers,

        // Utility functions
        getUserById,
        getUsersByRole,
        getActiveUsers,
        getUsersByDepartment,
        getUserProjects,
        getAvailableProjects,
        clearMessages,
        refreshData
    };

    return (
        <TeamManagementContext.Provider value={contextValue}>
            {children}
        </TeamManagementContext.Provider>
    );
};

export default TeamManagementProvider; 